import { Big } from 'big.js';
import { DisplayData, TenantOrderLine } from '~/lib/model';
import { schema_latest_version, ZodVersionedMetadata } from '~/lib/zod';
import { PersonaType } from '~/tenants/common/TenantPersona';
import { DeliverableType } from '~common/model/Deliverable';
import McmJob from '~/tenants/mcm/model/McmJob';
import McmEditingConfig from '~/tenants/mcm/performable/editing/McmEditingConfig';
import { TWT_EDITING_LABELS, TwtEditingType } from '~/tenants/twt/performable/editing/TwtEditingConfig';
import { TWT_EDITING_PAYOUT, TWT_EDITING_PRICES } from '~/tenants/twt/performable/editing/TwtEditingJob';

/**
 * An agent's edit request (McmEditingConfig): Twist's prices and payouts per
 * edit, done by the photographer who shot the photos, never offered in the
 * order form (hidden).
 */
export default class McmEditingJob extends McmJob<typeof McmEditingConfig> {
  get performable() {
    return McmEditingConfig;
  }

  get deliverable(): DeliverableType {
    return DeliverableType.PHOTO;
  }

  get hidden(): boolean {
    return true;
  }

  get confirm(): boolean {
    return true;
  }

  get configurable(): boolean {
    return true;
  }

  private get images() {
    return this.metadata.images.filter(
      (i) => !i.job_id || !this.appointment || this.appointment?.job_ids.includes(i.job_id),
    );
  }

  get inputs(): string[] {
    return this.images.map((i) => i.deliverable_id);
  }

  defaultValue(): ZodVersionedMetadata<(typeof McmEditingConfig)['schema']> {
    return {
      version: schema_latest_version(McmEditingConfig.schema),
      images: [],
    } as never;
  }

  get short() {
    return 'EDIT';
  }

  get media() {
    return PersonaType.PROVIDER;
  }

  info(): DisplayData[] {
    const info = super.info();

    for (const image of this.images) {
      info.push({
        name: `Input ${this.images.indexOf(image) + 1}`,
        value: `(${TWT_EDITING_LABELS[image.type]}) ${image.notes ?? 'No Notes'}`,
        deliverable_id: image.deliverable_id,
        provider: true,
      });
    }

    return info;
  }

  expenseLines(): TenantOrderLine[] {
    const lines = super.expenseLines();

    for (const image of this.images) {
      if (TWT_EDITING_PAYOUT[image.type as TwtEditingType]) {
        lines.push({
          amount: new Big(TWT_EDITING_PAYOUT[image.type as TwtEditingType]),
          description: TWT_EDITING_LABELS[image.type],
          id: `${image.deliverable_id}-${image.type}`,
        });
      }
    }

    return lines;
  }

  revenueLines(): TenantOrderLine[] {
    const lines = super.revenueLines();

    for (const image of this.images) {
      if (TWT_EDITING_PRICES[image.type as TwtEditingType]) {
        lines.push({
          amount: new Big(TWT_EDITING_PRICES[image.type as TwtEditingType]),
          description: TWT_EDITING_LABELS[image.type],
          taxable: true,
          id: `${image.deliverable_id}-${image.type}`,
        });
      }
    }

    return lines;
  }
}
